import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Request deduplication: track in-flight requests
const inFlightRequests = new Map();

// Retry configuration
const RETRY_DELAYS = [100, 250, 500]; // exponential backoff: 100ms, 250ms, 500ms
const RETRY_STATUS_CODES = [429, 503, 408]; // Too Many Requests, Service Unavailable, Request Timeout

/**
 * Execute request with deduplication and retry logic
 * If same request is in flight, return the same promise
 * If request fails with 429, retry with exponential backoff
 */
async function executeWithRetry(method, url, config = {}, retryAttempt = 0) {
  // Create a unique key for this request
  const requestKey = `${method}:${url}:${JSON.stringify(config.data || {})}`;

  // Check if this request is already in flight
  if (inFlightRequests.has(requestKey)) {
    return inFlightRequests.get(requestKey);
  }

  // Execute the request
  const requestPromise = (async () => {
    try {
      const response = await axios({
        method,
        url: `${API_URL}${url}`,
        ...config
      });
      
      // Remove from in-flight map on success
      inFlightRequests.delete(requestKey);
      return response;
    } catch (error) {
      // Remove from in-flight map on error
      inFlightRequests.delete(requestKey);

      // Check if we should retry
      if (RETRY_STATUS_CODES.includes(error.response?.status) && retryAttempt < RETRY_DELAYS.length) {
        const delay = RETRY_DELAYS[retryAttempt];
        console.warn(`[API] ${error.response?.status} on ${method} ${url}, retrying in ${delay}ms (attempt ${retryAttempt + 1})`);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Retry the request (not using cached promise this time)
        return executeWithRetry(method, url, config, retryAttempt + 1);
      }

      throw error;
    }
  })();

  // Store the promise to deduplicate future requests
  inFlightRequests.set(requestKey, requestPromise);

  return requestPromise;
}

/**
 * GET request with automatic deduplication and retry
 */
export const get = (url, config = {}) => {
  return executeWithRetry('GET', url, config);
};

/**
 * POST request with automatic deduplication and retry
 */
export const post = (url, data, config = {}) => {
  return executeWithRetry('POST', url, { ...config, data });
};

/**
 * PUT request with automatic deduplication and retry
 */
export const put = (url, data, config = {}) => {
  return executeWithRetry('PUT', url, { ...config, data });
};

/**
 * DELETE request with automatic deduplication and retry
 */
export const delete_ = (url, config = {}) => {
  return executeWithRetry('DELETE', url, config);
};

/**
 * Clear in-flight request cache (useful for testing or manual reset)
 */
export const clearCache = () => {
  inFlightRequests.clear();
};

/**
 * Create axios instance with automatic headers
 */
export const createAxiosInstance = (headers = {}) => {
  return axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      ...headers
    }
  });
};

export default {
  get,
  post,
  put,
  delete: delete_,
  clearCache,
  createAxiosInstance
};
